import React, { useState } from "react";
import {
  Box, Typography, Paper, TextField, Button,
  MenuItem, Select, FormControl, CircularProgress,
} from "@mui/material";
import MessageIcon   from "@mui/icons-material/Message";
import SendIcon      from "@mui/icons-material/Send";
import StarIcon      from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { submit_school_feedback_api } from "../../DAL/SchoolPortal/SchoolPortal";

const THEME = "#c01e2e";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: THEME, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputBase-input::placeholder": { color: "#aaa" },
};

const FEEDBACK_TYPES = [
  { id: "positive", label: "Positive" },
  { id: "neutral",  label: "Neutral"  },
  { id: "negative", label: "Negative" },
];

export default function SchoolFeedback() {
  const { userInfo }        = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({ feedbackType: "neutral", subject: "", message: "", rating: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [hoveredStar,  setHoveredStar]  = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message.trim()) {
      enqueueSnackbar("Please enter your feedback message", { variant: "error" });
      return;
    }
    setIsSubmitting(true);
    const userName = userInfo?.first_name && userInfo?.last_name
      ? `${userInfo.first_name} ${userInfo.last_name}`
      : userInfo?.name || userInfo?.email || "Anonymous";
    const typeLabel = FEEDBACK_TYPES.find((t) => t.id === formData.feedbackType)?.label || "Neutral";
    const subjectLine = formData.subject.trim() ? `[${typeLabel}] ${formData.subject.trim()}` : `[${typeLabel}]`;
    try {
      const res = await submit_school_feedback_api({
        name: userName, email: userInfo?.email || "anonymous@xair.app",
        userType: "flight_school_admin", userId: userInfo?._id || null,
        schoolId: userInfo?.flightSchool || null, schoolName: userInfo?.schoolName || null,
        category: "general", subject: subjectLine, message: formData.message.trim(),
        rating: formData.rating > 0 ? formData.rating : null,
      });
      if (res.success || res.code === 200) {
        setSubmitted(true);
        enqueueSnackbar("Thank you for your feedback!", { variant: "success" });
      } else throw new Error(res.error || "Failed to submit feedback");
    } catch (error) {
      enqueueSnackbar(error.message || "Something went wrong. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ feedbackType: "neutral", subject: "", message: "", rating: 0 });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Paper sx={{ p: 5, backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", textAlign: "center", maxWidth: 450 }}>
          <Box sx={{ width: 88, height: 88, borderRadius: "50%", backgroundColor: "rgba(192,30,46,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <CheckCircleIcon sx={{ fontSize: 44, color: THEME }} />
          </Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 2 }}>
            Thank You!
          </Typography>
          <Typography sx={{ fontSize: "15px", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", lineHeight: 1.6, mb: 4 }}>
            Your feedback has been submitted. We truly appreciate you taking the time to help us improve XAir.
          </Typography>
          <Button onClick={resetForm} variant="contained"
            sx={{ backgroundColor: THEME, color: "#fff", fontWeight: 600, px: 4, py: 1.5, borderRadius: "10px", textTransform: "none", fontFamily: "'Rubik', sans-serif", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
            Submit More Feedback
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, margin: "0 auto" }}>
      {/* Hero */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: "18px", backgroundColor: "rgba(192,30,46,0.08)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <MessageIcon sx={{ fontSize: 36, color: THEME }} />
        </Box>
        <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 1 }}>
          We Value Your Feedback
        </Typography>
        <Typography sx={{ fontSize: "15px", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>
          XAir is built by pilots, for pilots. Your feedback shapes every feature, every improvement, and every update we make.
        </Typography>
      </Box>

      {/* Form */}
      <Paper component="form" onSubmit={handleSubmit}
        sx={{ p: { xs: 3, sm: 4 }, backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>

        {/* Type */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", mb: 1 }}>Feedback Type</Typography>
          <FormControl fullWidth>
            <Select name="feedbackType" value={formData.feedbackType} onChange={handleInputChange}
              sx={{ backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a", fontFamily: "'Rubik', sans-serif",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: THEME, borderWidth: "1.5px" },
              }}>
              {FEEDBACK_TYPES.map((t) => <MenuItem key={t.id} value={t.id} sx={{ fontFamily: "'Rubik', sans-serif" }}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Subject */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", mb: 1 }}>Subject (Optional)</Typography>
          <TextField fullWidth name="subject" value={formData.subject} onChange={handleInputChange}
            placeholder="Brief summary of your feedback" sx={inputSx} />
        </Box>

        {/* Message */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", mb: 1 }}>Your Feedback *</Typography>
          <TextField fullWidth multiline rows={6} name="message" value={formData.message} onChange={handleInputChange}
            placeholder="Tell us what's on your mind. What do you love? What could be better?" sx={inputSx} />
        </Box>

        {/* Rating */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", mb: 1.5 }}>
            Overall experience? (Optional)
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Box key={star} onClick={() => setFormData((p) => ({ ...p, rating: star }))}
                onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)}
                sx={{ cursor: "pointer", transition: "transform 0.15s", "&:hover": { transform: "scale(1.15)" } }}>
                {star <= (hoveredStar || formData.rating)
                  ? <StarIcon sx={{ fontSize: 30, color: THEME }} />
                  : <StarBorderIcon sx={{ fontSize: 30, color: "#ccc" }} />
                }
              </Box>
            ))}
          </Box>
          {formData.rating > 0 && (
            <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 1 }}>
              {formData.rating === 1 && "We appreciate your honesty. Help us do better!"}
              {formData.rating === 2 && "Thanks for letting us know. We'll work on it!"}
              {formData.rating === 3 && "Good to know! We're always improving."}
              {formData.rating === 4 && "Great! We're glad you're enjoying XAir!"}
              {formData.rating === 5 && "Amazing! You're the best! 🎉"}
            </Typography>
          )}
        </Box>

        {/* Submit */}
        <Button type="submit" fullWidth disabled={isSubmitting} variant="contained"
          sx={{ backgroundColor: isSubmitting ? "rgba(192,30,46,0.5)" : THEME, color: "#fff", fontWeight: 600, py: 1.5, borderRadius: "10px", textTransform: "none", fontSize: "15px", fontFamily: "'Rubik', sans-serif", display: "flex", alignItems: "center", gap: 1, boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
          {isSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <><SendIcon sx={{ fontSize: 18 }} /> Submit Feedback</>}
        </Button>
      </Paper>
    </Box>
  );
}
